import React from 'react'
import { observer } from 'mobx-react-lite'
import { SimulationNodeDatum } from 'd3-force'

import { useBudgetsStore } from '../../common'
import { type DetailedBudget } from '../../core'

import {
  computeScaledNodes,
  enableDrag,
  resize,
  simulate,
  type SimulationType,
} from '../utils/BubblesSVG'

import { Bubble } from './bubblechart/Bubble'

export interface BubbleData extends DetailedBudget, SimulationNodeDatum {
  radius: number
}

interface BubbleChartProps {
  height: number
  width: number
  isDraggable?: boolean
  onClick?: (budget: DetailedBudget) => void
}

const BubbleChart: React.FC<BubbleChartProps> = ({
  height,
  width,
  isDraggable = false,
  onClick = () => {},
}) => {
  const [simulationNodes, setSimulationNodes] = React.useState<BubbleData[]>([])

  const simulationRef = React.useRef<SimulationType | null>(null)
  const dragEnabledRef = React.useRef<boolean>(false)

  const { detailedBudgets: budgets } = useBudgetsStore()

  const updateSimulation = React.useCallback(() => {
    const previousSimulation = simulationRef.current
    const currentSimulation = simulate(budgets)
    const currentNodes = currentSimulation.nodes()

    if (previousSimulation) {
      const previousNodes = previousSimulation.nodes()
      const previousNodesMap = new Map(previousNodes.map((node) => [node.guid, node]))

      currentNodes.forEach((node) => {
        const previousNode = previousNodesMap.get(node.guid)
        if (previousNode) {
          Object.assign(node, {
            vx: previousNode.vx,
            vy: previousNode.vy,
            x: previousNode.x,
            y: previousNode.y,
          })
        }
      })
    }

    simulationRef.current = currentSimulation
    dragEnabledRef.current = false
    setSimulationNodes(currentNodes)
  }, [budgets])

  React.useEffect(() => {
    updateSimulation()
  }, [updateSimulation])

  React.useEffect(() => {
    return () => {
      if (simulationRef.current) {
        simulationRef.current.stop()
        simulationRef.current = null
      }
    }
  }, [])

  React.useEffect(() => {
    const simulation = simulationRef.current
    if (!simulation) return

    const nodes = simulation.nodes()
    const isCompact = (width > 0 && width < 450) || (height > 0 && height < 300)

    const updatedNodes = computeScaledNodes(nodes, budgets, isCompact, height, width)
    simulation.nodes(updatedNodes)
    resize(simulation, height, width)

    if (isDraggable) {
      setTimeout(() => {
        enableDrag(simulation)
      }, 0)
    }

    setSimulationNodes([...updatedNodes])
  }, [height, width, isDraggable, budgets])

  return (
    <svg
      height={height}
      style={{ overflow: 'visible', touchAction: 'manipulation' }}
      width={Math.abs(width)}
    >
      {simulationNodes.map((bubble) => (
        <Bubble bubble={bubble} isDraggable={isDraggable} key={bubble.guid} onClick={onClick} />
      ))}
    </svg>
  )
}

export default observer(BubbleChart)
